import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { cognitoAdminService } from '../../services/cognitoAdminService';
import { getLogs } from '../../services/logService';

const C = {
  blue: '#2563eb', green: '#16a34a', purple: '#7c3aed', orange: '#d97706',
  text: '#1e293b', muted: '#64748b', border: '#e2e8f0', bg: '#f8fafc', card: '#fff',
};

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '-' : Number(n).toLocaleString('tr-TR'));

const th = {
  textAlign: 'left', padding: '9px 11px', background: C.bg, borderBottom: `2px solid ${C.border}`,
  fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
};
const td = { padding: '8px 11px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.text };

const IP5_STEPS = [
  { id: '5.1', title: '5.1 Kullanıcı deneyimi / arayüz tasarımı ve geri dönüşler', desc: 'Rol bazlı UX envanteri ve geri bildirim kanalları' },
  { id: '5.2', title: '5.2 Arayüzün yapay zeka ile ilişkilendirilerek test yayını', desc: 'YZ entegre ekranlar ve test yayın durumu' },
  { id: '5.3', title: '5.3 Kullanıcı testlerinden beta yayına alma', desc: 'Test bulguları → beta kabul checklist’i' },
  { id: 'C.5.1', title: 'Ç.5.1 Ürün arayüzü', desc: 'Canlı ürün ekran haritası (teslim çıktısı)' },
];

const Section = ({ step, children }) => (
  <div style={{ marginBottom: 28, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: 'linear-gradient(90deg,#faf5ff,#f8fafc)' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{step.title}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{step.desc}</div>
    </div>
    <div style={{ padding: 14 }}>{children}</div>
  </div>
);

/** Gerçek ürün ekran envanteri */
const PRODUCT_UI = [
  { modul: 'Giriş (Auth)', rota: '/login', roller: 'Tüm kullanıcılar', yz: 'Hayır', yayin: 'Prod', kategori: 'Kimlik' },
  { modul: 'Admin — Genel Bakış', rota: 'Admin / overview', roller: 'municipality-admin', yz: 'Hayır', yayin: 'Prod', kategori: 'Yönetim' },
  { modul: 'Park Yönetim', rota: 'Admin / parkmanager', roller: 'admin', yz: 'Hayır', yayin: 'Prod', kategori: 'Operasyon' },
  { modul: 'Uydu Analiz (NDVI)', rota: 'Admin / ndvi', roller: 'admin, landscape', yz: 'Evet — GEE/NDVI', yayin: 'Test/Prod', kategori: 'YZ / Uydu' },
  { modul: 'Canlı Veri (ASKİ)', rota: 'Admin / aski-live', roller: 'admin, technical', yz: 'Hayır', yayin: 'Prod', kategori: 'Veri' },
  { modul: 'Park Doğrulama', rota: 'Admin / park-verify', roller: 'admin', yz: 'Kısmi — eşleştirme', yayin: 'Prod', kategori: 'CBS' },
  { modul: 'Dönem Analizi', rota: 'Admin / aski-analysis', roller: 'admin, technical', yz: 'Hayır', yayin: 'Prod', kategori: 'Analiz' },
  { modul: 'GIS Analiz', rota: 'Admin / gis-analysis', roller: 'admin', yz: 'Kısmi — isim eşleştirme', yayin: 'Prod', kategori: 'CBS' },
  { modul: 'Rapor (IP2–IP5)', rota: 'Admin / rapor', roller: 'admin', yz: 'Evet — 13 metod + IP3/4', yayin: 'Test', kategori: 'YZ / Rapor' },
  { modul: 'AI Tahmin (Prophet)', rota: 'Admin / ai-forecast', roller: 'admin', yz: 'Evet — Prophet', yayin: 'Test/Prod', kategori: 'YZ' },
  { modul: 'Anomali Tespiti', rota: 'Admin / anomaly', roller: 'admin, technical', yz: 'Evet — 13 metod', yayin: 'Test/Prod', kategori: 'YZ' },
  { modul: 'IoT Cihazlar', rota: 'Admin / iot', roller: 'admin, technical', yz: 'Kısmi', yayin: 'Test', kategori: 'IoT' },
  { modul: 'Su Tüketimi Raporu', rota: 'Admin / water', roller: 'admin, technical', yz: 'Hayır', yayin: 'Prod', kategori: 'Rapor' },
  { modul: 'Kullanıcı Yönetimi', rota: 'Admin / users', roller: 'admin', yz: 'Hayır', yayin: 'Prod', kategori: 'Yönetim' },
  { modul: 'Sistem İzleme / Loglar', rota: 'Admin / monitoring', roller: 'admin', yz: 'Hayır', yayin: 'Prod', kategori: 'Operasyon' },
  { modul: 'Harita / CBS görünümü', rota: 'Map / KMZ', roller: 'technical, landscape', yz: 'Hayır', yayin: 'Prod', kategori: 'CBS' },
];

const FEEDBACK_CHANNELS = [
  { kanal: 'Uygulama içi destek e-posta', hedef: 'info@pronuve.com', durum: 'Aktif', not: 'Login ekranı destek linki' },
  { kanal: 'Cognito kullanıcı rolleri', hedef: 'admin / technical / landscape', durum: 'Aktif', not: 'Rol bazlı ekran yönlendirme' },
  { kanal: 'Sistem logları (CloudWatch/API)', hedef: '/api/logs', durum: 'Aktif', not: 'Giriş ve sistem olayları' },
  { kanal: 'Admin Rapor geri bildirim alanı', hedef: 'Admin / rapor', durum: 'Açık', not: 'IP5 test notları burada toplanır' },
];

const BETA_CHECKLIST = [
  { madde: 'Auth + rol bazlı erişim çalışıyor', kaynak: '5.1', durum: 'Tamam' },
  { madde: 'ASKİ canlı veri ve dönem analizi ekranda', kaynak: '5.2', durum: 'Tamam' },
  { madde: '13 anomali metodu Rapor’da gerçek veriyle', kaynak: '5.2 / IP4', durum: 'Tamam' },
  { madde: 'Uydu/NDVI YZ ekranı test edilebilir', kaynak: '5.2 / IP3', durum: 'Test' },
  { madde: 'AI Tahmin (Prophet) erişilebilir', kaynak: '5.2', durum: 'Test' },
  { madde: 'Kullanıcı geri bildirim kanalı tanımlı', kaynak: '5.1', durum: 'Tamam' },
  { madde: 'Kritik hata logları izlenebiliyor', kaynak: '5.3', durum: 'Tamam' },
  { madde: 'Beta kullanıcı grubu (Cognito) hazır', kaynak: '5.3', durum: 'Devam' },
];

const AdminRaporIP5 = () => {
  const { user, userRole } = useAuth();
  const [userCount, setUserCount] = useState(null);
  const [roleBreakdown, setRoleBreakdown] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackList, setFeedbackList] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pronuve_ip5_feedback') || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const users = await cognitoAdminService.listUsers().catch(() => []);
        if (!cancelled) {
          setUserCount(users.length);
          const roles = {};
          users.forEach((u) => {
            const attrs = u.Attributes || u.attributes || [];
            const get = (k) => {
              const hit = attrs.find((a) => a.Name === k || a.name === k);
              return hit?.Value || hit?.value;
            };
            const role = get('custom:role') || (u.Groups?.[0] || u.group || 'undefined');
            roles[role] = (roles[role] || 0) + 1;
          });
          // Groups often not on listUsers — fallback counts by enabled status
          if (Object.keys(roles).length <= 1) {
            const enabled = users.filter((u) => u.Enabled !== false).length;
            setRoleBreakdown([
              { rol: 'Toplam kayıtlı', adet: users.length },
              { rol: 'Aktif (Enabled)', adet: enabled },
              { rol: 'Pasif', adet: users.length - enabled },
            ]);
          } else {
            setRoleBreakdown(Object.entries(roles).map(([rol, adet]) => ({ rol, adet })));
          }
        }

        const logs = await getLogs(8, 0).catch(() => []);
        if (!cancelled) setRecentLogs(Array.isArray(logs) ? logs.slice(0, 8) : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveFeedback = () => {
    const text = feedbackNote.trim();
    if (!text) return;
    const item = {
      id: Date.now(),
      text,
      by: user?.email || user?.username || 'anon',
      role: userRole || '-',
      at: new Date().toLocaleString('tr-TR'),
    };
    const next = [item, ...feedbackList].slice(0, 50);
    setFeedbackList(next);
    localStorage.setItem('pronuve_ip5_feedback', JSON.stringify(next));
    setFeedbackNote('');
  };

  const aiModules = useMemo(
    () => PRODUCT_UI.filter((m) => String(m.yz).toLowerCase().includes('evet') || String(m.yz).toLowerCase().includes('kısmi')),
    []
  );

  const publishSummary = useMemo(() => {
    const prod = PRODUCT_UI.filter((m) => m.yayin.includes('Prod')).length;
    const test = PRODUCT_UI.filter((m) => m.yayin.includes('Test')).length;
    return { total: PRODUCT_UI.length, prod, test, ai: aiModules.length };
  }, [aiModules]);

  return (
    <div style={{ marginTop: 36 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
          İş Paketi 5 — Kullanıcı Arayüzü Tasarımı
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
          5.1 → 5.3 ve Ç.5.1 — canlı ürün arayüzü envanteri, YZ bağlantıları, test/beta durumu
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
        {[
          { label: 'Kayıtlı kullanıcı', val: loading ? '…' : fmt(userCount), color: C.blue },
          { label: 'Ürün ekranı', val: publishSummary.total, color: C.text },
          { label: 'YZ ilişkili ekran', val: publishSummary.ai, color: C.purple },
          { label: 'Geri bildirim kaydı', val: feedbackList.length, color: C.green },
        ].map((c) => (
          <div key={c.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color, marginTop: 4 }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* 5.1 */}
      <Section step={IP5_STEPS[0]}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Rol / kullanıcı dağılımı (Cognito)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr>
              {['Rol / Durum', 'Adet'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(roleBreakdown.length ? roleBreakdown : [{ rol: 'Veri yükleniyor / erişilemedi', adet: '-' }]).map((r, i) => (
              <tr key={r.rol} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={td}>{r.rol}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt(r.adet)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Geri bildirim kanalları</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr>
              {['Kanal', 'Hedef', 'Durum', 'Not'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {FEEDBACK_CHANNELS.map((r, i) => (
              <tr key={r.kanal} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.kanal}</td>
                <td style={td}>{r.hedef}</td>
                <td style={{ ...td, color: C.green, fontWeight: 700 }}>{r.durum}</td>
                <td style={td}>{r.not}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>UX geri bildirim formu (yerel kayıt)</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            value={feedbackNote}
            onChange={(e) => setFeedbackNote(e.target.value)}
            placeholder="Kullanıcı / test geri bildirimi yazın…"
            style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12 }}
          />
          <button
            type="button"
            onClick={saveFeedback}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: C.blue, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
          >
            Kaydet
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Tarih', 'Kullanıcı', 'Rol', 'Geri bildirim'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {feedbackList.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, color: C.muted, textAlign: 'center' }}>Henüz kayıt yok</td></tr>
            )}
            {feedbackList.map((f, i) => (
              <tr key={f.id} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontSize: 11 }}>{f.at}</td>
                <td style={td}>{f.by}</td>
                <td style={td}>{f.role}</td>
                <td style={td}>{f.text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* 5.2 */}
      <Section step={IP5_STEPS[1]}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          Yapay zeka ile ilişkilendirilmiş arayüzler ve test yayın durumu.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Modül', 'Rota', 'YZ Bağlantısı', 'Yayın', 'Kategori'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {aiModules.map((m, i) => (
              <tr key={m.modul} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontWeight: 600 }}>{m.modul}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{m.rota}</td>
                <td style={{ ...td, color: C.purple, fontWeight: 600 }}>{m.yz}</td>
                <td style={td}>
                  <span style={{
                    padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
                    background: m.yayin.includes('Prod') ? '#dcfce7' : '#ffedd5',
                    color: m.yayin.includes('Prod') ? C.green : C.orange,
                  }}>{m.yayin}</span>
                </td>
                <td style={td}>{m.kategori}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* 5.3 */}
      <Section step={IP5_STEPS[2]}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Beta yayın checklist</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr>
              {['Madde', 'Kaynak', 'Durum'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {BETA_CHECKLIST.map((r, i) => (
              <tr key={r.madde} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={td}>{r.madde}</td>
                <td style={td}>{r.kaynak}</td>
                <td style={{
                  ...td, fontWeight: 700,
                  color: r.durum === 'Tamam' ? C.green : r.durum === 'Test' ? C.orange : C.blue,
                }}>{r.durum}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Son sistem logları (test izleme)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Zaman', 'Seviye', 'Kategori', 'Mesaj'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {recentLogs.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, color: C.muted, textAlign: 'center' }}>Log yok veya API erişilemedi</td></tr>
            )}
            {recentLogs.map((log, i) => (
              <tr key={log.id || i} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, fontSize: 11 }}>{log.created_at || log.timestamp || log.time || '-'}</td>
                <td style={td}>{log.level || '-'}</td>
                <td style={td}>{log.category || '-'}</td>
                <td style={{ ...td, fontSize: 11 }}>{log.message || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Ç.5.1 */}
      <Section step={IP5_STEPS[3]}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          Teslim çıktısı: web tabanlı Pronuve ürün arayüzü — mevcut canlı ekran haritası.
          Oturum: <strong>{user?.email || user?.username || '-'}</strong> · Rol: <strong>{userRole || '-'}</strong>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['#', 'Modül', 'Rota / Sekme', 'Hedef roller', 'YZ', 'Yayın', 'Kategori'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PRODUCT_UI.map((m, i) => (
              <tr key={m.modul} style={{ background: i % 2 ? C.bg : C.card }}>
                <td style={{ ...td, color: C.muted }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 600 }}>{m.modul}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{m.rota}</td>
                <td style={td}>{m.roller}</td>
                <td style={td}>{m.yz}</td>
                <td style={td}>{m.yayin}</td>
                <td style={td}>{m.kategori}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export default AdminRaporIP5;
